import type { StatsigGateId } from '~/configs/statsig'

/**
 * One-line summaries shown next to each gate in the feature flag panel.
 *
 * Typed as a total `Record`, so adding a member to {@link StatsigGateId}
 * without describing it here is a compile error and the panel can never
 * render a bare ID with no explanation. The full rationale for each gate
 * stays on the union in `~/configs/statsig`; these are the short forms for
 * someone scanning the whole list.
 */
export const GATE_LABELS: Record<StatsigGateId, string> = {
  'billing-column-sorting': 'Server-side column sorting on the billing invoice and payment tables',
  'invoice-column-sorting': 'Superseded by billing-column-sorting; read only during the rename',
  'products-v2': 'v2 products & agreements UI',
  'invoice-templates': 'Invoice template builder pages and nav entry',
  'asset-sharing': 'Asset Sharing settings page and nav entry',
  'entity-context-links': 'Entity pages use EntityContextLinks instead of Breadcrumbs',
  reports_v3: 'Route report views through the reports-api backend',
  report_hub: 'Consolidated Reports hub (off = legacy report builder)',
  'curtailment-ui': 'All curtailment surfaces on the battery detail page',
  'eng-10029-generator-controls': 'Generator Controls tab — the only gate on generator commands',
  'eng-13756-manifest-generator-controls':
    'Generator Controls tab — render the commands the Link advertises (off = legacy per-OEM lists)',
  'eng-14111-engine-snapshot-tile': 'Engine tile (starter battery and engine readings) on the generator Snapshot tab',
  'curtailment-override': 'Edit affordances within the curtailment UI',
  'eng-9018-curtailment-ui-on-site-page': 'Rebuilt Site Utilization chart (needs curtailment-ui)',
  'eng-9686-vehicle-detail-chart-mui': 'Rebuilt vehicle detail chart (Apex → MUI X)',
  'use-vehicle-min-soc': 'Vehicle min-SoC field on vehicle forms',
  'use-vehicle-max-soc': 'Vehicle max-SoC field on vehicle forms',
  'use-vehicle-commissioning-status': 'Vehicle commissioning-status field and schedule timeline',
  'ai-assistant': 'Chat AI assistant bar',
  'assistant-v2': 'Assistant chat served by assistant-api instead of the syn-ops bar (needs ai-assistant)',
  'assistant-usage': 'Assistant Usage settings page and Commerce nav entry',
  'generative-dashboard': 'Generative dashboard page and nav entry (Intelligence → Composer)',
  'billing-composer': 'Compose with Agent panel on product/agreement pages + setup card (needs ai-assistant)',
  'external-proxy-charger': 'External Proxy Mode toggle on charger Metadata tab',
  'configuration-history': 'OCPP configuration snapshot history on charger History tab',
  expanded_charger_history: 'Redesigned Configuration History timeline',
  'tax-management': 'Billing tax management surfaces',
  'tax-coverage': 'Tax hub Overview — coverage score, gaps, setup stepper (needs tax-management)',
  'tax-suggested-rates': 'Suggested rates on a tax coverage gap (needs tax-coverage)',
  'operations-ui': 'Ticket prefix and Operations email intake',
  'advanced-firmware': 'Platform firmware catalog surfaces',
  'eng-9535_includedeleted_flag': 'Request deleted vehicles — reports-api reads this gate too',
  uber_dashboard_demo_tile: 'Site Usage Breakdown dashboard tile (demo)',
  queued_vehicles: 'Queued-vehicles chip and overlay on charger Connectors panel',
  'eng-10134-display-energy-system-view': 'Rebuilt Power & Uptime card with DER asset sections',
  'eng-10237-demo-mode': 'Demo mode entry points (settings + Cmd+K)',
  'eng-10448-fullscreen-dashboard': 'Fullscreen "monitor" toggle on the dashboard',
  'metered-panels-in-the-ui': 'Metered Panels section inside the Power & Uptime card',
  'eng-10729-energy-systems-ui': 'Site-scoped energy-systems route and entry point',
  'eng-11717-billing-hub-customers': 'Billing Hub Customers tab and per-customer drill-in',
  'energy-system-builder': 'Edit Site Energy System tab: place assets into the energy system',
  'eng-8347-asset-notes': 'Asset Notes flag band, drawer and header entry points',
  'eng-12503_chargerfaultsexport_flag':
    'Faults & Warnings export via reports-api (off = page-only CSV); reports-api gates the endpoint on the same flag',
  'eng-11721_billingexports_flag':
    'Billing invoices, payments and payouts full CSV export via reports-api (off = page-only CSV); reports-api gates the endpoints on the same flag',
  'eng-12609_ocpplogsexport_flag':
    'Site-wide OCPP log export card on the site Tools tab; reports-api gates the endpoint on the same flag',
  synop_internal_only: 'Synop-internal affordances — the audience selector for this panel',
  'batteries-in-ui': 'Batteries entity: nav, routes, settings CRUD',
  'fuel-tanks-in-ui': 'Fuel tanks entity: nav, routes, settings CRUD',
  'grid-connections-in-ui': 'Grid connections entity: nav, routes, settings CRUD',
  'loads-in-ui': 'Loads entity: nav, routes, settings CRUD',
  'meters-in-the-ui': 'Meters entity: nav, routes, readings panels',
  'panels-in-ui': 'Panels entity: nav, routes, site architecture surfaces',
  'solar-in-ui': 'Solar arrays entity: nav, routes, site map cards',
  integrations: 'Integrations catalog nav entry and routes',
  'battery-sim-management': 'Battery simulator controls (also needs derSimulator URL)',
  'solar-sim-management': 'Solar simulator controls (also needs derSimulator URL)',
  'vehicle-sim-management': 'Vehicle simulator management surfaces',
  vehicle_detail_refresh: 'Vehicle detail command center + Highland day schedule',
  'virtual-charger': 'Virtual Chargers tab on the site detail page',
  'device-connectivity-in-ui': 'Device Connectivity section on Link detail',
  'device-health-in-ui': 'Device Health section on Link detail',
  'network-diagnostics-in-ui': 'Network Diagnostics section on Link detail (internal-only)',
  'ocpp-log-analyzer': 'OCPP log analysis tab (hidden from read-only users regardless)',
  'log-analysis-model-format-selection': 'Model and format pickers in the log-analysis prompt',
  'detailed-profile-statuses': 'Expanded per-status columns in profile history',
  site_controls_advanced: 'Advanced site charge-management controls',
  'eng-7863-node-ramp-rate-field': 'Ramp-rate field on site edit node rows',
  'eng-8762-trickle-charge-initial-step': 'Trickle-charge initial-step field on site edit',
  'eng-12965-dynamic-node-limit-fields': 'Dynamic node limit fields on site edit node settings',
  'eng-8369-dashboard-vehicle-ready-status': 'Vehicle ready-status in the Connector Overview tile',
  'public-charging-instructions': 'Public charging instructions on the site OCPI tab',
  'homecharging-payee-deactivate': 'Deactivate action on home-charging payees',
  'eng-7101-move-user': 'Move-user action on the user edit page',
  'eng-8181-transfer-sso': 'Transfer-SSO action on the org SSO status card',
  'i18n-translation-qa': 'Translation QA FAB that blanks or randomizes copy',
  'network-config-in-ui': 'Network card on the Link detail page',
  'network-config-write': 'Write actions in the Network card (needs network-config-in-ui)',
  'eng-13004-link-ocpp-proxy-settings': 'OCPP Proxy card on the Link page and its Edit Link tab',
  'live-power-flows': 'Live power-flow layer fed by the live-data WebSocket',
  'efficiency-forecasting': 'Vehicle Efficiency and Charging tabs and the bulk efficiency model reset',
  'eng-13182-plan-inspector': 'Plan Inspector tab on the ChargeAI page',
  'eng-13511-transaction-plan-tab': 'Plan tab on the charging transaction overlay',
  'desktop-app': 'Desktop app (alpha)',
  'site-power-chart': 'Site Power card in place of Site Utilization on the site page',
  org_hierarchy_bar: 'Org hierarchy bar (path crumbs + Owned by) instead of the cloak banner'
}

/**
 * Gates a backend service evaluates independently. Overriding these changes
 * what the browser renders while the service keeps its own answer, so the panel
 * says so rather than letting the mismatch read as a bug.
 */
export const SERVER_EVALUATED_GATES: ReadonlySet<StatsigGateId> = new Set<StatsigGateId>([
  'eng-9535_includedeleted_flag',
  'reports_v3'
])

export const ALL_GATE_IDS = Object.keys(GATE_LABELS).sort() as StatsigGateId[]
